import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus, MapPin, Pencil, Trash2, Eye, QrCode } from "lucide-react";
import { toast } from "sonner";
import QRCodeDialog from "@/components/QRCodeDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";

const STATUS = {
  pending: { text: "En revisión", className: "bg-amber-100 text-amber-900" },
  approved: { text: "Aprobado", className: "bg-emerald-100 text-emerald-900" },
  rejected: { text: "Rechazado", className: "bg-rose-100 text-rose-900" },
  sold: { text: "Vendido", className: "bg-slate-200 text-slate-800" },
};

export default function MyListingsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/listings/mine").then(r => setItems(r.data || [])).finally(()=> setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Publicación eliminada");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  return (
    <div className="bg-slate-50 min-h-[80vh]">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-blue-950">Mis publicaciones</h1>
          <Link to="/ofrecer">
            <Button className="bg-blue-600 hover:bg-blue-700 h-12 rounded-xl" data-testid="my-listings-new-btn">
              <Plus className="w-5 h-5 mr-1.5" /> Nueva
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="text-slate-500 py-10 text-center">Cargando…</div>
        ) : items.length === 0 ? (
          <Card className="p-10 rounded-2xl border border-dashed border-slate-300 text-center">
            <p className="text-slate-700 font-medium">Aún no tienes publicaciones</p>
            <p className="text-sm text-slate-500 mt-1">Publica tu primer medicamento y ayuda a otra familia.</p>
            <Link to="/ofrecer" className="inline-block mt-4">
              <Button className="bg-blue-600 hover:bg-blue-700 h-12 rounded-xl">
                <Plus className="w-5 h-5 mr-1.5" /> Crear publicación
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((it) => (
              <Card key={it.id} className="rounded-2xl overflow-hidden border border-slate-200 bg-white flex flex-col" data-testid={`my-listing-${it.id}`}>
                <Link to={`/medicamento/${it.id}`} className="block">
                  <div className="aspect-[4/3] bg-slate-100">
                    <img src={it.photo_front} alt={it.medicine_name} className="w-full h-full object-cover" />
                  </div>
                </Link>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display font-semibold text-base text-slate-900 line-clamp-2">{it.medicine_name}</h3>
                    <Badge className={`${STATUS[it.status]?.className} hover:${STATUS[it.status]?.className}`}>{STATUS[it.status]?.text}</Badge>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-slate-500"><MapPin className="w-3.5 h-3.5 inline mr-1"/>{it.zone}</span>
                    <span className="font-semibold text-blue-900">${it.base_cost.toFixed(0)} MXN</span>
                  </div>
                  <div className="mt-4 flex gap-2 pt-3 border-t border-slate-100">
                    <Link to={`/medicamento/${it.id}`} className="flex-1" data-testid={`my-listing-view-${it.id}`}>
                      <Button variant="outline" size="sm" className="w-full"><Eye className="w-4 h-4 mr-1"/>Ver</Button>
                    </Link>
                    <Link to={`/ofrecer/${it.id}`} className="flex-1" data-testid={`my-listing-edit-${it.id}`}>
                      <Button variant="outline" size="sm" className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"><Pencil className="w-4 h-4 mr-1"/>Editar</Button>
                    </Link>
                    <QRCodeDialog
                      listingId={it.id}
                      medicineName={it.medicine_name}
                      price={it.base_cost?.toFixed(0)}
                      zone={it.zone}
                      trigger={
                        <Button variant="outline" size="sm" className="border-blue-200 text-blue-700 hover:bg-blue-50" data-testid={`my-listing-qr-${it.id}`}>
                          <QrCode className="w-4 h-4" />
                        </Button>
                      }
                    />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`my-listing-delete-${it.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar publicación?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción no se puede deshacer. <strong>{it.medicine_name}</strong> dejará de estar disponible.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(it.id)} className="bg-rose-600 hover:bg-rose-700" data-testid={`my-listing-delete-confirm-${it.id}`}>
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
